import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { HttpStatusCode } from '@angular/common/http';
import { CustomMessageService, LocalStorageService, ResponseHttpError, ResponseHttpSuccess, UtilService } from 'library';
import { finalize } from 'rxjs';
import { LocalStorageKey } from 'projects/store/src/app/enums';
import {
  SellerTraceabilityColumnFilters,
  SellerTraceabilityFilters,
  SellerTraceabilityItem,
  SellerTraceabilityStatusOption,
  UserData
} from 'projects/store/src/app/interfaces';
import { RequestService } from 'projects/store/src/app/services/api/v1/request.service';

@Component({
  selector: 'app-seller-traceability-report',
  templateUrl: './seller-traceability-report.component.html',
  styleUrls: ['./seller-traceability-report.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SellerTraceabilityReportComponent implements OnInit {
  list: SellerTraceabilityItem[] = [];
  filteredList: SellerTraceabilityItem[] = [];
  startDate?: Date;
  endDate?: Date;
  loading = false;
  readonly rows = 15;
  statusOptions: SellerTraceabilityStatusOption[] = [];
  columnFilters: SellerTraceabilityColumnFilters = this.getEmptyColumnFilters();

  private readonly userData: UserData;

  constructor(private utilService: UtilService,
              private localStorageService: LocalStorageService,
              private requestService: RequestService,
              private customMessageService: CustomMessageService,
              private changeDetectorRef: ChangeDetectorRef) {
    this.userData = this.localStorageService.get(LocalStorageKey.data);
  }

  ngOnInit(): void {
    this.utilService.disablePageLoading();
    this.load();
  }

  load(filters: SellerTraceabilityFilters = {}): void {
    const storeId = this.userData.store?.id;

    if (!storeId) {
      this.list = [];
      this.filteredList = [];
      return;
    }

    this.loading = true;
    const queryString = this.buildSellerTraceabilityQueryString(filters);

    this.requestService.getSellerTraceability(storeId, queryString)
      .pipe(finalize(() => {
        this.loading = false;
        this.changeDetectorRef.markForCheck();
      }))
      .subscribe({
        next: (resp: ResponseHttpSuccess) => {
          if (resp.status === HttpStatusCode.Ok) {
            this.list = resp.body.data.list ?? [];
            this.statusOptions = this.getStatusOptions();
            this.applyColumnFilters();
            this.changeDetectorRef.markForCheck();
          } else {
            this.customMessageService.responseNoticeHandler(resp);
          }
        },
        error: (error: ResponseHttpError) => {
          this.customMessageService.responseNoticeHandler(error);
        }
      });
  }

  filter(): void {
    this.load({
      start_date: this.startDate,
      end_date: this.endDate
    });
  }

  clearFilters(): void {
    this.startDate = undefined;
    this.endDate = undefined;
    this.columnFilters = this.getEmptyColumnFilters();
    this.load();
  }

  applyColumnFilters(): void {
    this.filteredList = this.list.filter(row => {
      return this.matchesDate(row.date)
        && this.matchesText(row.credit_number, this.columnFilters.credit_number)
        && this.matchesText(row.customer_number, this.columnFilters.customer_number)
        && this.matchesText(row.document, this.columnFilters.document)
        && this.matchesText(row.first_name, this.columnFilters.first_name)
        && this.matchesText(row.last_name, this.columnFilters.last_name)
        && this.matchesText(row.initial_seller, this.columnFilters.initial_seller)
        && this.matchesText(row.continuation_seller, this.columnFilters.continuation_seller)
        && this.matchesStatus(row.status);
    });
  }

  private getEmptyColumnFilters(): SellerTraceabilityColumnFilters {
    return {
      credit_number: '',
      customer_number: '',
      document: '',
      first_name: '',
      last_name: '',
      initial_seller: '',
      continuation_seller: '',
      status: ''
    };
  }

  private getStatusOptions(): SellerTraceabilityStatusOption[] {
    const uniqueStatuses = Array.from(new Set(this.list.map(row => row.status).filter(Boolean)));

    return [
      { label: 'Todos', value: '' },
      ...uniqueStatuses.map(status => ({ label: status, value: status }))
    ];
  }

  private matchesDate(date: string): boolean {
    if (!this.columnFilters.date) {
      return true;
    }

    return this.formatDateValue(date) === this.formatDateValue(this.columnFilters.date);
  }

  private matchesText(value: string, filter: string): boolean {
    const normalizedFilter = this.normalizeText(filter);

    if (!normalizedFilter) {
      return true;
    }

    return this.normalizeText(value).includes(normalizedFilter);
  }

  private matchesStatus(status: string): boolean {
    return !this.columnFilters.status || status === this.columnFilters.status;
  }

  private normalizeText(value?: string): string {
    return `${value ?? ''}`.trim().toLowerCase();
  }

  private buildSellerTraceabilityQueryString(filters: SellerTraceabilityFilters): string {
    const params = Object.entries(filters)
      .filter(([, value]) => value !== undefined && value !== null && value !== '')
      .map(([key, value]) => `${key}=${encodeURIComponent(this.formatDateValue(value as string | Date))}`);

    return params.length > 0 ? `?${params.join('&')}` : '';
  }

  private formatDateValue(value: string | Date): string {
    if (typeof value === 'string') {
      return value.split('T')[0];
    }

    const date = value instanceof Date ? value : new Date(value);
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');

    return `${date.getFullYear()}-${month}-${day}`;
  }
}
